import { useInfiniteQuery, useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type {
  ClientContextCourseLessonEntry,
  Course,
  DeleteEligibility,
  Lesson,
  ListResponse,
  PublishLifecycleState,
  PublishState,
  WorkflowState,
} from '@course-authoring/shared-types';
import {
  fetchWithAuthRetry,
  buildListSearchParams,
  normalizeApiError,
  parseApiError,
  type ListQueryParams,
} from './apiUtils';
import type { TagSummary } from './tagApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const LESSON_LIBRARY_PAGE_SIZE = 50;

export const courseRecordKeys = {
  all: ['courseRecords'] as const,
  lists: () => [...courseRecordKeys.all, 'list'] as const,
  list: (params: ListQueryParams) => [...courseRecordKeys.lists(), params] as const,
  details: () => [...courseRecordKeys.all, 'detail'] as const,
  // accountId is part of the key so base and client-context reads never collide.
  detail: (id: string, locale = 'en', accountId: number | null = null) =>
    [...courseRecordKeys.details(), id, locale, accountId ?? 'base'] as const,
};

export const lessonListKeys = {
  all: ['lessons', 'list'] as const,
  list: (params: ListQueryParams) => [...lessonListKeys.all, params] as const,
};

export const lessonLibraryKeys = {
  all: ['lessonLibrary'] as const,
  lists: () => [...lessonLibraryKeys.all, 'list'] as const,
  search: (search: string, assignedAccountId: number | null = null) =>
    [...lessonLibraryKeys.lists(), search, assignedAccountId ?? 'all'] as const,
};

const LESSON_COPY_TARGET_PAGE_SIZE = 25;

export const lessonCopyTargetKeys = {
  all: ['lessons', 'copyTargets'] as const,
  search: (search: string, excludeLessonId: string) =>
    [...lessonCopyTargetKeys.all, search, excludeLessonId] as const,
};

export const lessonUsageKeys = {
  all: ['lessons', 'usage'] as const,
  detail: (lessonId: string) => [...lessonUsageKeys.all, lessonId] as const,
};

export interface CourseRecordApiError {
  status: number;
  code: string;
  message: string;
  retryable: boolean;
}

export type CreateCourseInput = Partial<Pick<
  Course,
  | 'title'
  | 'description'
  | 'lessonIds'
  | 'timedCourse'
  | 'durationMinutes'
  | 'minimumRequiredMinutes'
  | 'thumbnailSrc'
  | 'thumbnailAlt'
  | 'completionCertificateEnabled'
  | 'certificateMessage'
  | 'completedMessage'
>>;

export type UpdateCourseInput = Partial<Pick<
  Course,
  | 'title'
  | 'description'
  | 'lessonIds'
  | 'timedCourse'
  | 'durationMinutes'
  | 'minimumRequiredMinutes'
  | 'thumbnailSrc'
  | 'thumbnailAlt'
  | 'completionCertificateEnabled'
  | 'certificateMessage'
  | 'completedMessage'
>> & {
  /**
   * Client-context composition entries in resolved display order (suppressed
   * base rows included). Only valid on a client-context save — the account id
   * itself is injected by useUpdateCourse from its accountId option.
   */
  courseLessons?: ClientContextCourseLessonEntry[];
};

export interface UpdateCourseResponse extends Course {
  /** tmp → server course-lesson id mappings from a client-context save. */
  idMappings?: Record<string, string>;
}

/**
 * NA-296: `updateCourse`'s response never carries `publishState` (it's a
 * separate concern the detail GET serializes) — the cache write below must
 * preserve whatever was previously cached rather than blank it. But a
 * successful save also means the session is no longer cancelable server-
 * side, same one-way transition as the lesson equivalent in courseApi.ts's
 * `withEditedPublishState`. `CourseEditorPage`'s sync effect skips when this
 * comes back `undefined`, so an absent previous value stays `undefined` —
 * not forced to `null` — to preserve that skip.
 */
export function withEditedCoursePublishState(previous: { publishState?: PublishState } | undefined): PublishState | undefined {
  return previous?.publishState
    ? { ...previous.publishState, canCancelEdit: false }
    : previous?.publishState;
}

export interface CourseListResponseItem {
  id: string;
  numericId: number;
  title: string;
  /** NA-449: standard (Emtrain) title; only in a client context, when it differs from `title`. */
  standardTitle?: string;
  workflowState: WorkflowState;
  publishLifecycle: PublishLifecycleState;
  createdAt: string;
  assignedAccountIds: string[];
  timedCourse: boolean;
  deleteEligibility: DeleteEligibility;
  /** Present only when a tagIds filter is active — the subset that matched. */
  tags?: TagSummary[];
}

export interface LessonListResponseItem {
  id: string;
  numericId: number;
  title: string;
  /** NA-449: standard (Emtrain) title; only in a client context, when it differs from `title`. */
  standardTitle?: string;
  description: string;
  estimatedTimeMinutes?: number;
  workflowState: WorkflowState;
  publishLifecycle: PublishLifecycleState;
  createdAt: string;
  assignedAccountIds: string[];
  courseUsageCount: number;
  deleteEligibility: DeleteEligibility;
  /** Present only when a tagIds filter is active — the subset that matched. */
  tags?: TagSummary[];
}

function toCourseRecordApiError(error: unknown): CourseRecordApiError {
  if (error && typeof error === 'object' && 'status' in error) {
    return error as CourseRecordApiError;
  }
  return {
    status: 0,
    code: 'NETWORK_ERROR',
    message: error instanceof Error ? error.message : 'Network request failed',
    retryable: true,
  };
}

function toApiError(response: Response, fallbackMessage: string): CourseRecordApiError {
  return {
    status: response.status,
    code: `HTTP_${response.status}`,
    message: response.statusText || fallbackMessage,
    retryable: response.status >= 500 || response.status === 429,
  };
}

async function fetchCourses(params: ListQueryParams): Promise<ListResponse<CourseListResponseItem>> {
  const sp = buildListSearchParams(params);
  const qs = sp.toString();
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses${qs ? `?${qs}` : ''}`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch courses');
  return response.json();
}

async function fetchLessons(params: ListQueryParams): Promise<ListResponse<LessonListResponseItem>> {
  const sp = buildListSearchParams(params);
  const qs = sp.toString();
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons${qs ? `?${qs}` : ''}`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch lessons');
  return response.json();
}

async function fetchCourse(id: string, locale = 'en', accountId: number | null = null): Promise<Course> {
  const params = new URLSearchParams({ locale });
  if (accountId !== null) params.set('accountId', String(accountId));
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${id}?${params}`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch course');
  return response.json();
}

async function createCourse(input: CreateCourseInput): Promise<Course> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses`, {
    method: 'POST',
    body: JSON.stringify({
      title: input.title?.trim() || 'Untitled Course',
      description: input.description ?? '',
      lessonIds: input.lessonIds ?? [],
      timedCourse: input.timedCourse ?? false,
      durationMinutes: input.durationMinutes ?? null,
      minimumRequiredMinutes: input.minimumRequiredMinutes ?? null,
      thumbnailSrc: input.thumbnailSrc ?? '',
      thumbnailAlt: input.thumbnailAlt ?? '',
      completionCertificateEnabled: input.completionCertificateEnabled ?? true,
      certificateMessage: input.certificateMessage ?? '',
      completedMessage: input.completedMessage ?? '',
    }),
  });

  if (!response.ok) throw toApiError(response, 'Failed to create course');
  return response.json();
}

async function updateCourse(
  id: string,
  input: UpdateCourseInput,
  locale = 'en',
  accountId: number | null = null,
): Promise<UpdateCourseResponse> {
  // The backend reads the client-context account from the request body.
  const body = accountId !== null ? { ...input, accountId } : input;
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${id}?${new URLSearchParams({ locale })}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

  if (!response.ok) throw toApiError(response, 'Failed to update course');
  return response.json();
}

export function useCourses(params: ListQueryParams = {}) {
  return useQuery({
    queryKey: courseRecordKeys.list(params),
    queryFn: () => fetchCourses(params),
    placeholderData: keepPreviousData,
  });
}

export function useLessonList(params: ListQueryParams = {}) {
  return useQuery({
    queryKey: lessonListKeys.list(params),
    queryFn: () => fetchLessons(params),
    placeholderData: keepPreviousData,
  });
}

export interface LessonUsageCourse {
  /** Obfuscated id — used for routing/links. */
  courseId: string;
  /** Raw auto-increment id — used for the "C{n}" display id. */
  courseNumericId: number;
  title: string;
  workflowState: WorkflowState;
  /** Lifecycle nuance behind the pill ('editing' ⇒ "Published — editing"). */
  publishLifecycle?: PublishLifecycleState;
}

export interface LessonCourseUsage {
  count: number;
  courses: LessonUsageCourse[];
}

export const EMPTY_LESSON_COURSE_USAGE: LessonCourseUsage = { count: 0, courses: [] };

export async function fetchLessonUsage(lessonId: string): Promise<LessonCourseUsage> {
  let response: Response;
  try {
    response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/usage`);
  } catch (error) {
    // A dropped connection rejects with a bare TypeError; normalize it so the
    // retry predicate sees retryable: true instead of skipping retries.
    throw toCourseRecordApiError(error);
  }
  if (!response.ok) throw toApiError(response, 'Failed to fetch lesson usage');
  return response.json();
}

/** NA-284: the courses referencing one lesson — the "Used in Courses" panel. */
export function useLessonUsageQuery(lessonId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: lessonUsageKeys.detail(lessonId ?? ''),
    queryFn: () => fetchLessonUsage(lessonId as string),
    enabled: (options?.enabled ?? true) && lessonId !== null,
    // Override the app-wide carry-previous-data default: without this,
    // opening lesson B's panel briefly shows lesson A's courses.
    placeholderData: undefined,
    // Only retry errors marked retryable (5xx/429) — a 404 should surface
    // immediately, not after four attempts (same predicate as metadata reads).
    retry: (failureCount, error: unknown) =>
      Boolean(error && typeof error === 'object' && 'retryable' in error && (error as CourseRecordApiError).retryable)
      && failureCount < 3,
  });
}

export function useCourse(id: string, options?: { enabled?: boolean; locale?: string; accountId?: number | null }) {
  const locale = options?.locale ?? 'en';
  const accountId = options?.accountId ?? null;
  return useQuery({
    queryKey: courseRecordKeys.detail(id, locale, accountId),
    queryFn: () => fetchCourse(id, locale, accountId),
    enabled: options?.enabled ?? Boolean(id),
    placeholderData: undefined,
  });
}

export function useCreateCourse(options?: {
  onSuccess?: (data: Course) => void;
  onError?: (error: CourseRecordApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCourseInput) => createCourse(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: courseRecordKeys.lists() });
      queryClient.setQueryData(courseRecordKeys.detail(data.id), data);
      // Composition changed — lesson "Used In" counts may be stale (NA-284).
      // Usage details are REMOVED, not invalidated: the data is known-changed,
      // and a removed entry makes the panel's next open show a real loading
      // state instead of flashing the outdated rows while refetching.
      queryClient.invalidateQueries({ queryKey: lessonListKeys.all });
      queryClient.removeQueries({ queryKey: lessonUsageKeys.all });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      options?.onError?.(toCourseRecordApiError(error));
    },
  });
}

export function useUpdateCourse(options?: {
  locale?: string;
  accountId?: number | null;
  onSuccess?: (data: UpdateCourseResponse) => void;
  onError?: (error: CourseRecordApiError) => void;
}) {
  const queryClient = useQueryClient();
  const locale = options?.locale ?? 'en';
  const accountId = options?.accountId ?? null;

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCourseInput }) =>
      updateCourse(id, input, locale, accountId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: courseRecordKeys.lists() });
      const detailKey = courseRecordKeys.detail(data.id, locale, accountId);
      const previous = queryClient.getQueryData<UpdateCourseResponse>(detailKey);
      queryClient.setQueryData<UpdateCourseResponse>(detailKey, {
        ...data,
        publishState: withEditedCoursePublishState(previous),
      });
      // Inheritance is one-way (mirror of the lesson save mutation): a base
      // save ripples into every client-context view of this course (metadata
      // + composition for non-customized scopes), but a client save is
      // account-scoped and never affects base or other clients. So only a
      // base save needs to refresh the OTHER cached variants for this course.
      //
      // Use removeQueries (not invalidate) for the inactive sibling variants:
      // the editor seeds its draft once per hydration scope, and a merely-
      // stale-but-present cache would let the seed grab the stale copy on
      // context switch and then ignore the background refetch. Dropping the
      // cache forces a clean fetch + fresh seed. `type: 'inactive'` leaves the
      // active editor's own query (seeded above) untouched.
      if (accountId === null) {
        queryClient.removeQueries({
          queryKey: [...courseRecordKeys.details(), data.id],
          type: 'inactive',
        });
        // NA-284: base composition changed — refresh Used In counts, and
        // REMOVE (not invalidate) usage details: the data is known-changed,
        // so the panel's next open should load fresh rather than flash the
        // outdated rows while refetching. Client-context saves touch only
        // account anchors, which base-scoped usage deliberately ignores.
        queryClient.invalidateQueries({ queryKey: lessonListKeys.all });
        queryClient.removeQueries({ queryKey: lessonUsageKeys.all });
      }
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      options?.onError?.(toCourseRecordApiError(error));
    },
  });
}

async function deleteCourse(id: string): Promise<void> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${id}`, { method: 'DELETE' });
  // Body-aware: the refusal's typed code and message drive the author-facing
  // reason, unlike the status-only errors the read paths build.
  if (!response.ok) throw await parseApiError(response, 'Failed to delete course');
}

/**
 * NA-283 course deletion. Eligibility is server-computed and enforced, so this
 * hook's job is cache hygiene: the course is gone, and its lessons' "Used In"
 * data changed with it (lesson-course-usage requires a delete to evict the
 * same caches a composition change does).
 */
export function useDeleteCourseMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: CourseRecordApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCourse(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: courseRecordKeys.lists() });
      queryClient.removeQueries({ queryKey: [...courseRecordKeys.details(), id] });
      // Usage details are REMOVED, not invalidated (same rationale as the
      // create/update paths): the data is known-changed, so the panel's next
      // open loads fresh instead of flashing the deleted course.
      queryClient.invalidateQueries({ queryKey: lessonListKeys.all });
      queryClient.removeQueries({ queryKey: lessonUsageKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: unknown, id: string) => {
      const apiError = normalizeApiError(error, 'Failed to delete course');
      // A refusal means the caller rendered against stale eligibility —
      // refresh both the list rows and the detail read behind the editor top
      // bar so Delete comes back disabled rather than inviting a retry.
      if (apiError.status === 409) {
        queryClient.invalidateQueries({ queryKey: courseRecordKeys.lists() });
        queryClient.invalidateQueries({ queryKey: [...courseRecordKeys.details(), id] });
      }
      options?.onError?.(apiError);
    },
  });
}

export function useLessonLibrary(search = '', assignedAccountId: number | null = null) {
  return useInfiniteQuery({
    queryKey: lessonLibraryKeys.search(search, assignedAccountId),
    queryFn: ({ pageParam = 1 }) =>
      fetchLessons({
        page: pageParam as number,
        pageSize: LESSON_LIBRARY_PAGE_SIZE,
        search,
        ...(assignedAccountId !== null ? { assignedAccountId: String(assignedAccountId) } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}

/**
 * NA-437: destination search for the "Copy to another lesson" picker.
 * Emtrain lessons only (`excludeAccountOnly`), excludes the lesson currently
 * open (`excludeLessonId`), and ranks an exact-id search match first —
 * all server-enforced via the same opt-in `listLessons` flags.
 */
export function useLessonCopyTargets(search: string, excludeLessonId: string) {
  return useInfiniteQuery({
    queryKey: lessonCopyTargetKeys.search(search, excludeLessonId),
    queryFn: ({ pageParam = 1 }) =>
      fetchLessons({
        page: pageParam as number,
        pageSize: LESSON_COPY_TARGET_PAGE_SIZE,
        search,
        excludeAccountOnly: true,
        excludeLessonId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return fetched < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    staleTime: 0,
  });
}
